import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, finalize, map, Observable, of, startWith, switchMap, tap } from 'rxjs';

import { PLAYERS_CONTACT_PATH } from '../../../../shared/players/players.constants';
import { PLAYERS_COPY } from '../../../../shared/players/players.copy';
import type { OwnPlayerProfile } from '../../../../shared/players/players.model';
import { PlayersService } from '../../../../shared/players/players.service';
import { playersSessionIdentity } from '../../../../shared/players/players-session';
import { PlayRequestsComponent } from './play-requests/play-requests.component';
import { PlayerProfileFormComponent } from './player-profile-form/player-profile-form.component';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'loaded'; profile: OwnPlayerProfile | null };

/**
 * Pestaña «Comunidad» de `/perfil` (B11 J2): el perfil de jugador de la cuenta.
 *
 * Contrato con B10 (§5.5 de su spec): sin entradas, la carga la ruta y lee sus propios
 * servicios; la sesión la resuelve el contenedor de B10 (un 401 lo deja «sin sesión»,
 * aquí solo cae al estado de error con «Reintentar»). Estados (D19): skeleton · alta
 * (`null`) · edición con su estado de visibilidad · error. Borrar pide confirmación en la
 * propia pestaña, nunca `window.confirm` (D24). Debajo, las bandejas de J3 (`app-play-requests`).
 *
 * **Atada a la cuenta (Astra H1)**: la carga cuelga de una clave `id` de la cuenta +
 * «Reintentar», con `switchMap`, como el directorio de J1. Si la cuenta cambia de A a B sin
 * pasar por `null` (el contenedor de B10 sigue montado), el `GET` de A se cancela, el estado
 * vuelve a `loading` y se pide el perfil de B. Con `null` no se pide nada. Las escrituras
 * comprueban además, al responder, que la cuenta sigue siendo la misma.
 *
 * **Guardar y Borrar se excluyen (Astra H2)**: «Borrar» se deshabilita mientras el formulario
 * guarda (`busyChange`), y el formulario se bloquea (`locked`) con la confirmación abierta o
 * borrando. Además, un `saved` de un guardado que empezó antes de un borrado se ignora
 * (época de borrado): nunca resucita un perfil borrado.
 */
@Component({
  selector: 'app-tab-community',
  imports: [PlayerProfileFormComponent, PlayRequestsComponent, RouterLink],
  templateUrl: './tab-community.component.html',
  styleUrl: './tab-community.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class TabCommunityComponent {
  private readonly players = inject(PlayersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /** Foco de la confirmación: entra en «Cancelar» (la opción segura) y vuelve al botón al cerrar. */
  private readonly deleteButton = viewChild<ElementRef<HTMLButtonElement>>('deleteButton');
  private readonly cancelDeleteButton = viewChild<ElementRef<HTMLButtonElement>>('cancelDeleteButton');

  protected readonly copy = PLAYERS_COPY.profile;
  protected readonly bannerCopy = PLAYERS_COPY.banner;
  protected readonly contactPath = PLAYERS_CONTACT_PATH;

  protected readonly state = signal<LoadState>({ kind: 'loading' });
  protected readonly confirmingDelete = signal(false);
  protected readonly deleting = signal(false);
  protected readonly notice = signal<{ tone: 'info' | 'error'; text: string } | null>(null);

  /** El formulario está guardando: «Borrar» se deshabilita (H2). */
  protected readonly formBusy = signal(false);
  /** Con la confirmación abierta o borrando, el formulario no guarda (H2). */
  protected readonly formLocked = computed(() => this.confirmingDelete() || this.deleting());

  /**
   * Cuenta a la que está atada la pestaña (H1): el `id`, no el objeto `User`. `null` también
   * mientras `auth.me()` está en vuelo (identidad pendiente, Astra J3 H4): tras el login de
   * otra cuenta el token ya es el nuevo pero `currentUser` sigue siendo el anterior.
   */
  private readonly accountId = playersSessionIdentity().accountId;
  private readonly retryTick = signal(0);
  /** Sube con cada borrado y cada cambio de cuenta; un guardado de otra época no cuenta (H2). */
  private deleteEpoch = 0;
  private saveEpoch = -1;

  protected readonly profile = computed(() => {
    const state = this.state();
    return state.kind === 'loaded' ? state.profile : null;
  });

  /** Frase de estado bajo el título (D19). */
  protected readonly visibility = computed<'visible' | 'suspended' | 'hidden' | null>(() => {
    const profile = this.profile();
    if (!profile) return null;
    if (profile.suspended || profile.hidden_reason === 'suspended') return 'suspended';
    return profile.visible ? 'visible' : 'hidden';
  });

  protected readonly hiddenText = computed(() => {
    const reason = this.profile()?.hidden_reason;
    return reason && reason !== 'suspended' ? this.copy.hiddenReason[reason] : '';
  });

  constructor() {
    const key = computed(() => ({ account: this.accountId(), tick: this.retryTick() }));
    toObservable(key)
      .pipe(
        // Cada clave nueva empieza de cero. Con `loading` el `@switch` deja de pintar el
        // formulario: se destruye y su `PUT` en vuelo se cancela (`takeUntilDestroyed`).
        tap(() => this.resetForNewKey()),
        switchMap(({ account }): Observable<LoadState> => {
          if (account === null) return of({ kind: 'loading' });
          return this.players.getMe().pipe(
            map((profile): LoadState => ({ kind: 'loaded', profile })),
            catchError(() => of<LoadState>({ kind: 'error' })),
            startWith<LoadState>({ kind: 'loading' }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((state) => this.state.set(state));
  }

  protected retry(): void {
    this.retryTick.update((tick) => tick + 1);
  }

  protected onFormBusy(busy: boolean): void {
    this.formBusy.set(busy);
    if (busy) this.saveEpoch = this.deleteEpoch;
  }

  protected onSaved(profile: OwnPlayerProfile): void {
    // Un guardado que empezó antes de un borrado (o de otra cuenta) no repinta nada.
    if (this.saveEpoch !== this.deleteEpoch) return;
    this.notice.set(null);
    this.state.set({ kind: 'loaded', profile });
  }

  private resetForNewKey(): void {
    this.deleteEpoch++;
    this.notice.set(null);
    this.confirmingDelete.set(false);
    this.deleting.set(false);
    this.formBusy.set(false);
    this.state.set({ kind: 'loading' });
  }

  protected askDelete(): void {
    if (this.formBusy()) return;
    this.notice.set(null);
    this.confirmingDelete.set(true);
    afterNextRender(() => this.cancelDeleteButton()?.nativeElement.focus(), { injector: this.injector });
  }

  protected cancelDelete(): void {
    this.confirmingDelete.set(false);
    afterNextRender(() => this.deleteButton()?.nativeElement.focus(), { injector: this.injector });
  }

  protected confirmDelete(): void {
    if (this.deleting()) return;
    const account = this.accountId();
    const epoch = ++this.deleteEpoch;
    const stillSame = () => this.accountId() === account && this.deleteEpoch === epoch;
    this.deleting.set(true);
    this.players
      .deleteMe()
      .pipe(
        finalize(() => {
          if (stillSame()) this.deleting.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (message) => {
          if (!stillSame()) return;
          this.confirmingDelete.set(false);
          this.state.set({ kind: 'loaded', profile: null });
          this.notice.set({ tone: 'info', text: message ?? this.copy.deletedFallback });
        },
        error: () => {
          if (!stillSame()) return;
          this.confirmingDelete.set(false);
          this.notice.set({ tone: 'error', text: this.copy.deleteError });
        },
      });
  }
}
